/* eslint-disable no-plusplus */
/* eslint-disable no-console */
// @flow

import { delay, range, map, isEqual, isEmpty } from 'lodash';
import cookie from 'react-cookies';
import {
  getCastReceiverContext,
  getSenders,
  RECEIVER_APP_PATH,
  DEFAULT_POSTER_URL,
} from 'Common/cast/CastUtils';
import {
  CustomPartialMessagesManager,
  CastPlayerHandler,
  LiveLinearPlaybackManager,
  SlingSeekbarModifier,
  CastReceiver,
} from 'Browser/cast/receiver';

import {
  fetchLiveVideoMetadata,
  fetchVideoMetadata,
  resetPlayer,
  postToRecents,
  putToRecents,
} from 'Common/player/actions';
import { fetchFranchise } from 'Common/franchise/actions';
import { setCurrentProfileId } from 'Common/profiles/actions';
import { getJson } from 'Common/utilities/fetchUtils';
import type { Store } from 'redux';
import type { State, BaseAction, ThunkAction, Dispatch } from 'Common/types';
import type { SourceType } from 'Common/player/types';
import type { Chunk } from 'Browser/cast/receiver/CustomMessagesReader';
import { getFreeWheelAdVmap } from 'Common/lib/player/managers/OnDemandAdManager';

import type {
  Cast,
  Metadata,
  CastSenderToReceiverMessageType,
  CastReceiverToSenderMessageType,
  CastContentConfig,
  AustinEvent,
  SubtitlesConf,
  AustinProgramInfoRequest,
} from 'Common/cast/types';
import type { RadishSessionParams } from 'Common/auth/types';
import StatsPostCastAnalytics from 'Browser/cast/receiver/analytics/StatsPostCastAnalytics';
import NielsenCastAnalytics from 'Browser/cast/receiver/analytics/NielsenCastAnalytics';
import ComscoreCastAnalytics from 'Browser/cast/receiver/analytics/ComscoreCastAnalytics';
import ConvivaCastAnalytics from 'Browser/cast/receiver/analytics/ConvivaCastAnalytics';
import AdobeCastAnalytics from 'Browser/cast/receiver/analytics/AdobeCastAnalytics';
import BaseCastAnalytics from 'Browser/cast/receiver/analytics/BaseCastAnalytics';
import { isSlingSourceType, isLiveLinearSourceType } from 'Common/models/franchise';
import VmCastAnalytics from 'Browser/cast/receiver/analytics/VmCastAnalytics';

import { TRANSITIONS } from './LiveLinearPlaybackManager';
import { addVmapToMedia } from './CastAdUtils';
import { AdInfo } from './AdSlotInfo';

declare var cast: Cast;

// $FlowFixMe - direct import (instead of conditional "require") breaks webpack build) */
// const Austin = process.env.IN_CHROMECAST_MODE ? require('austinjs').default : {};
const Austin = process.env.IN_CHROMECAST_MODE ? require('Common/player/assets/austin.debug').default : {};

/* MEDIA ELEMENT SPEC:
   https://html.spec.whatwg.org/multipage/media.html#best-practices-for-authors-using-media-elements */

class ChromeCastReceiver {
  static CUSTOM_NS = 'urn:x-cast:custom_ns';

  context: Object;
  playerManager: Object;
  queueManager: Object;
  messageReader: CustomPartialMessagesManager;
  castPlayerHandler: CastPlayerHandler;
  sourceType: SourceType = 'none';
  receiverId: string;
  uuid: string;
  isSignedIn: boolean = false;
  currentSlingEventInfo: { programTitle: string, episodeTitle: string, contentId: string };
  austinMetadataPollTimerId: TimeoutID;
  austinTimelineUpdateTimerId: TimeoutID;
  putToRecentsIntervalId: IntervalID;
  isAustinConfiguring: boolean;
  isAustinLoading: boolean;
  austinConfigureTimeoutId: TimeoutID;
  liveLinearPlaybackManager: LiveLinearPlaybackManager;
  slingSeekbarModifier: SlingSeekbarModifier;
  slug: string;
  channelLogo: string;
  analytics: Array<Object>;
  analyticsData: Object;
  isContentLoaded: boolean;
  playbackStartPosition: number | void;
  isAdBreakInProgress: boolean;
  _canPause: boolean = true;
  _seekEnabled: boolean = true;

  onAppStateChange: (event: { type: string, isBuffering?: boolean }) => void;
  onReceiveMetadata: Metadata => void;
  onPlaybackFinishedHandler: () => void;
  onAustinStateChange: (event: AustinEvent) => void;
  passEventToStateMachine: string => void;
  resetStore: Object => void;
  getRadishSession: RadishSessionParams => void;
  store: Store<State, BaseAction | ThunkAction, Dispatch>;

  static sendMessageToAllSenders(
    message: CastReceiverToSenderMessageType,
    onSend?: (senderId: string) => void,
  ) {
    const context = getCastReceiverContext();
    const senders = getSenders();

    senders.forEach(({ id: senderId }) => {
      context.sendCustomMessage(ChromeCastReceiver.CUSTOM_NS, senderId, message);
      if (onSend) onSend(senderId);
    });
  }

  static sendMessageToSender(senderId: string, message: CastReceiverToSenderMessageType) {
    const context = getCastReceiverContext();

    context.sendCustomMessage(ChromeCastReceiver.CUSTOM_NS, senderId, message);
  }

  constructor(
    store: Store<State, BaseAction | ThunkAction, Dispatch>,
    resetStore: () => void,
    playerKey: string,
    onPlaybackFinishedHandler: () => void,
    onAustinStateChange: (event: AustinEvent) => void,
    passEventToStateMachine: string => void,
    onAppStateChange: (event: { type: string, isBuffering?: boolean }) => void,
    onReceiveMetadata: Metadata => void,
    getRadishSession: () => void,
    setPlayerStream: Object => void,
    setDVRStream: Object => void,
    setLiveStream: Object => void,
  ) {
    this.store = store;
    this.resetStore = resetStore;
    this.passEventToStateMachine = passEventToStateMachine;

    this.onPlaybackFinishedHandler = onPlaybackFinishedHandler;
    this.onAustinStateChange = (event: AustinEvent) => {
      if (event.type === 'onStallEnd') {
        // This event comes when stream starts playing after initial load or changing channel.
        this.isAustinLoading = false;
        this.isAustinConfiguring = false;
      }
      onAustinStateChange(event);
    };
    this.onAppStateChange = onAppStateChange;
    this.onReceiveMetadata = onReceiveMetadata;
    this.getRadishSession = getRadishSession;

    this.context = getCastReceiverContext();
    this.messageReader = new CustomPartialMessagesManager();

    this.playerManager = this.context.getPlayerManager();
    this.queueManager = this.playerManager.getQueueManager();

    this.castPlayerHandler = new CastPlayerHandler(
      this,
      playerKey,
      setPlayerStream,
      setDVRStream,
      setLiveStream,
      passEventToStateMachine,
      this.onAustinStateChange,
    );

    this._updateStyles();

    this.liveLinearPlaybackManager = new LiveLinearPlaybackManager(this.store, this);
    this.slingSeekbarModifier = new SlingSeekbarModifier();
    this.analytics = [
      new ComscoreCastAnalytics(this.playerManager, this.store),
      new ConvivaCastAnalytics(this.playerManager, this.store),
      new VmCastAnalytics(this.playerManager, this.store),
      new AdobeCastAnalytics(this.playerManager, this.store),
      new NielsenCastAnalytics(this.playerManager, this.store),
      new StatsPostCastAnalytics(this.playerManager, this.store),
    ];
  }

  start() {
    this.enableLogging();
    this.subscribeToCustomMessages();
    this.subscribeToShutdownEvent();
    this.subscribeToErrorEvents();
    this.subscribeToSenderDisconnectedEvents();
    this.subscribeToSenderConnectedEvents();
    this.subscribeToPlayerManagerEvents();
    this.setTimeAliveTimer();
    this.setMessageInterception();

    this.context.start(this.getContextOptions());
  }

  _updateStyles() {
    // Update style using javascript
    const playerElement = document.getElementsByTagName('cast-media-player')[0];
    const { shadowRoot } = playerElement;
    const mediaElement = (shadowRoot: any).getElementById('castMediaElement');

    playerElement.style.setProperty('--next-preview-visibility', 'hidden');
    playerElement.style.setProperty(
      '--play-image',
      `url(${require('../../player/images/play-button.png')})`,
    );
    mediaElement.style.setProperty('object-fit', 'fill');
  }

  enableLogging(level: string = cast.framework.LoggerLevel.WARNING) {
    getCastReceiverContext().setLoggerLevel(
      process.env.IN_DEBUG ? level : cast.framework.LoggerLevel.NONE,
    );
  }

  subscribeToCustomMessages() {
    this.context.addCustomMessageListener(ChromeCastReceiver.CUSTOM_NS, msg => {
      if (msg.data.partialData === true) {
        this.handlePartialData(msg.data);
      } else {
        this.handleCustomMessage(msg.data);
      }
    });
  }

  subscribeToShutdownEvent() {
    this.context.addEventListener(cast.framework.system.EventType.SHUTDOWN, evt => {
      const playingSling = isSlingSourceType(this.sourceType);
      if (playingSling) this.castPlayerHandler.destroyCurrentPlayer(playingSling, !playingSling);

      CastReceiver.log('[CAST] Received SHUT DOWN event:', evt);
    });
  }

  subscribeToErrorEvents() {
    this.context.addEventListener(cast.framework.system.EventType.ERROR, evt =>
      console.error('[CAST] ERROR:', evt),
    );
  }

  subscribeToSenderDisconnectedEvents() {
    this.context.addEventListener(cast.framework.system.EventType.SENDER_DISCONNECTED, evt => {
      CastReceiver.log('[CAST] Sender disconnected:', evt);
    });
  }

  subscribeToSenderConnectedEvents() {
    this.context.addEventListener(
      cast.framework.system.EventType.SENDER_CONNECTED,
      ({ senderId }) => {
        CastReceiver.log('[CAST] Sender connected:', senderId);

        ChromeCastReceiver.sendMessageToSender(senderId, {
          type: 'CURRENT_SOURCE_TYPE',
          payload: { sourceType: this.sourceType },
        });
      },
    );
  }

  subscribeToPlayerManagerEvents() {
    this.playerManager.addEventListener(
      cast.framework.events.category.CORE,
      this.onAppStateChangeEvent,
    );
  }

  _getFranchise = async () => {
    const state = this.store.getState();
    const {
      franchise: {
        franchiseDetails: { bySlug },
      },
    } = state;

    let franchise = bySlug[this.slug];

    if (!franchise) {
      franchise = (await this.store.dispatch(fetchFranchise(this.slug))) || {};
    }
    return franchise;
  };

  _postToRecents = () => {
    const {
      player: { contentId },
    } = this.store.getState();

    const recentsInfo = {
      contentSource: 'ott',
      content_id: contentId,
      totalSeconds: Math.floor(this.playerManager.getDurationSec()),
      progressSeconds: Math.floor(this.playbackStartPosition || 0),
    };

    this.store.dispatch(postToRecents(recentsInfo));
  };

  _putToRecents = () => {
    const {
      player: { contentId, recentVideoId },
    } = this.store.getState();

    const recentsInfo = {
      contentSource: 'ott',
      content_id: contentId,
      totalSeconds: Math.floor(this.playerManager.getDurationSec()),
      progressSeconds: Math.floor(this.playerManager.getCurrentTimeSec()),
    };

    this.store.dispatch(putToRecents(recentVideoId, recentsInfo));
  };

  _setMetadataImages = (metadata, posterUrl: string, portraitUrl: string, secondaryUrl: string) => {
    metadata.posterUrl = posterUrl || DEFAULT_POSTER_URL;
    // The first image must be not large as receiver uses it as
    // a small metadata image on its UI
    metadata.images = [
      { width: 3, height: 2, url: secondaryUrl || DEFAULT_POSTER_URL },
      { width: 2, height: 3, url: portraitUrl || DEFAULT_POSTER_URL },
    ];
  };

  _setMetadata = async (metadata: cast.framework.messages.MovieMediaMetadata) => {
    const franchise = await this._getFranchise();
    if (isEmpty(franchise)) return;

    const { title, subtitle, images, name } = franchise;

    metadata.title = title || metadata.title || name;
    metadata.subtitle = subtitle || metadata.subtitle || name;

    if (metadata.subtitle == metadata.title) metadata.subtitle = '';

    const {
      poster_url: posterUrl,
      wide_poster_url: widePosterUrl,
      thumbnail_url: thumbnailUrl,
      portrait_url: portraitUrl,
    } = images || {};

    this._setMetadataImages(
      metadata,
      widePosterUrl || posterUrl || thumbnailUrl || this.channelLogo,
      portraitUrl || widePosterUrl || posterUrl || thumbnailUrl || this.channelLogo,
      posterUrl || thumbnailUrl || this.channelLogo || portraitUrl,
    );

    if (isSlingSourceType(this.sourceType)) {
      this.onReceiveMetadata({
        posterUrl: metadata.posterUrl,
        title: metadata.title,
        subtitle: metadata.subtitle,
      });
    }
  };

  _isAdPlaying = () =>
    this.isAdBreakInProgress || // Any Ad playing by CAF
    (isLiveLinearSourceType(this.sourceType) && this.liveLinearPlaybackManager.isPlayingAd); // LL custom DAI Ad

  async configureMediaItem(
    media: cast.framework.messages.MediaInformation,
    sourceType: SourceType | 'ad',
    { channelInformation = {}, stream = {} }: any,
  ) {
    const isSling = isSlingSourceType(sourceType);

    if (isSling) {
      media.streamType = cast.framework.messages.StreamType.LIVE;
      media.contentUrl = `${RECEIVER_APP_PATH}/black_loading_screen.mp4`;
    } else if (sourceType === 'ad') {
      media.contentType = 'video/mp4';
      // media.hlsSegmentFormat = cast.framework.messages.HlsSegmentFormat.FMP4;
      // media.hlsVideoSegmentFormat = cast.framework.messages.HlsVideoSegmentFormat.FMP4;
      media.streamType = cast.framework.messages.StreamType.BUFFERED;
    } else {
      const { linear_callsign: linearCallsign, callsigns, callsign } = channelInformation;
      const {
        source: { dash, hls, progressive },
        drm: { widevine },
        ruid, // EST content ID
        video_id: videoId,
      } = stream;

      if (sourceType !== 'trailer') {
        const ids = {};
        switch (sourceType) {
          case 'est':
            ids.asset_id = ruid.toString();
            break;
          case 'on_demand':
            ids.asset_id = videoId.toString();
            break;
          case 'live_linear':
            ids.channel_id = linearCallsign || callsigns || callsign;
            break;
          default:
        }

        const playbackConfig = new cast.framework.PlaybackConfig();
        // Customize the license url for playback
        playbackConfig.licenseUrl = widevine;
        playbackConfig.protectionSystem = cast.framework.ContentProtection.WIDEVINE;
        playbackConfig.licenseRequestHandler = requestInfo => {
          const message = btoa(
            String.fromCharCode.apply(null, new Uint8Array(requestInfo.content)),
          );
          // requestInfo.withCredentials = true;
          requestInfo.content = JSON.stringify({
            client: `DishAnywhere-Web/chromecast`,
            radish_host: this.store.getState().config.session.radish_url,
            user_id: cookie.load('rs'),
            ...ids,
            message,
          });
        };
        this.playerManager.setPlaybackConfig(playbackConfig);

        const inbandTrack = new cast.framework.messages.Track(
          1,
          cast.framework.messages.TrackType.TEXT,
        );
        inbandTrack.trackContentType = cast.framework.messages.CaptionMimeType.CEA608;
        inbandTrack.language = 'en';
        media.tracks = [inbandTrack];

        media.streamType = cast.framework.messages.StreamType.BUFFERED;
        media.contentUrl = hls.replace(/fairplay/, 'widevine_aac');
        media.hlsSegmentFormat = cast.framework.messages.HlsSegmentFormat.FMP4;
        media.hlsVideoSegmentFormat = cast.framework.messages.HlsVideoSegmentFormat.FMP4;
      } else {
        media.contentUrl = progressive;
      }
      if (media.contentId !== TRANSITIONS.LL_TRANSITION) {
        media.contentId = undefined;
      }
      media.contentType = 'video/mp4';
    }
  }

  async scheduleQueueItem(
    streamUrl: string,
    itemType: string,
    startTime: number = 0,
    duration: number = 0,
  ): cast.framework.messages.MediaInformation {
    const media = new cast.framework.messages.MediaInformation();
    media.metadata = new cast.framework.messages.MovieMediaMetadata();
    media.contentUrl = streamUrl;
    media.duration = duration;
    await this.scheduleQueueItemMedia(media, itemType, startTime);
    return media;
  }

  async scheduleQueueItemMedia(
    media: cast.framework.messages.MediaInformation,
    itemType: string,
    startTime: number = 0,
  ) {
    const item = new cast.framework.messages.QueueItem();
    item.media = media;
    item.startTime = startTime;
    item.preloadTime = 999999;
    item.media.contentId = itemType;

    if (itemType === TRANSITIONS.LL_TRANSITION) {
      const state = this.store.getState();

      const {
        player: {
          nextQvtPayload: {
            playback_info: {
              linear_info: { channel_call_sign: callsign },
              asset: { _href: assetMetadataUrl, episode_title: episodeTitle, title },
            },
            playback_info: qvtPlaybackInfo,
          },
          stream: { drm = {} } = {},
        },
      } = state;

      this.configureMediaItem(item.media, 'live_linear', {
        channelInformation: { callsign },
        stream: { source: { hls: item.media.contentUrl }, drm },
      });

      let assetMetadata;
      // remove first if block once SMS completely removes _href from assets
      if (assetMetadataUrl) {
        assetMetadata = await getJson({ url: assetMetadataUrl, state });
      } else {
        assetMetadata = qvtPlaybackInfo;
      }

      const {
        title: _1,
        program: {
          name: _2,
          franchise_image: { url: posterUrl } = {},
          thumbnail: { url: widePosterUrl } = {},
        } = {},
        thumbnail: { url: thumbnailUrl } = {},
      } = assetMetadata || {};

      item.media.metadata.title = title;
      item.media.metadata.subtitle = episodeTitle;

      this._setMetadataImages(
        item.media.metadata,
        widePosterUrl || posterUrl || thumbnailUrl || this.channelLogo,
        widePosterUrl || posterUrl || thumbnailUrl || this.channelLogo,
        posterUrl || thumbnailUrl || this.channelLogo,
      );
    } else if (
      itemType === TRANSITIONS.LL_TO_AD_TRANSITION ||
      itemType === TRANSITIONS.AD_TO_AD_TRANSITION
    ) {
      this.configureMediaItem(item.media, 'ad', {});
    }

    this.queueManager.insertItems([item]);
  }

  getContextOptions() {
    const options = new cast.framework.CastReceiverOptions();
    options.customNamespaces = {
      [ChromeCastReceiver.CUSTOM_NS]: cast.framework.system.MessageType.JSON,
    };

    // Set to true - for MPL, false - for Shaka
    // options.useLegacyDashSupport = true;
    const playbackConfig = new cast.framework.PlaybackConfig();
    playbackConfig.autoResumeDuration = 5;
    playbackConfig.protectionSystem = cast.framework.ContentProtection.WIDEVINE;
    playbackConfig.manifestRequestHandler = requestInfo => {
      requestInfo.url = requestInfo.url.split('%20').join('');
    };
    options.playbackConfig = playbackConfig;
    // For development purposes:
    // options.disableIdleTimeout = true;
    // options.maxInactivity = 3600;

    return options;
  }

  setMessageInterception() {
    this.playerManager.setMessageInterceptor(
      cast.framework.messages.MessageType.MEDIA_STATUS,
      this.interceptMediaStatus,
    );
    this.playerManager.setMessageInterceptor(
      cast.framework.messages.MessageType.LOAD,
      this.interceptLoad,
    );
    this.playerManager.setMessageInterceptor(
      cast.framework.messages.MessageType.SEEK,
      this.interceptSeek,
    );
    this.playerManager.setMessageInterceptor(
      cast.framework.messages.MessageType.PLAY,
      this.interceptPlay,
    );
    this.playerManager.setMessageInterceptor(
      cast.framework.messages.MessageType.PAUSE,
      this.interceptPause,
    );
    this.playerManager.setMessageInterceptor(
      cast.framework.messages.MessageType.EDIT_TRACKS_INFO,
      this.interceptCaptions,
    );
  }

  interceptLoad = async (loadRequestData: cast.framework.messages.LoadRequestData) => {
    const {
      media: { metadata, contentId },
      type,
    } = loadRequestData;

    const state = this.store.getState();

    loadRequestData.autoplay = true;
    this._canPause = true;
    this._seekEnabled = true;

    if (type === 'PRELOAD') {
      CastReceiver.log(`[CAST][PLAYER_MANAGER] PRELOAD (${JSON.stringify(metadata)}):`);
      return loadRequestData;
    }

    if (
      contentId === TRANSITIONS.LL_TO_AD_TRANSITION ||
      contentId === TRANSITIONS.AD_TO_AD_TRANSITION ||
      contentId === TRANSITIONS.AD_TO_LL_TRANSITION
    ) {
      return loadRequestData;
    }
    this.isContentLoaded = false;

    if (contentId !== TRANSITIONS.LL_TRANSITION) {
      const source: CastContentConfig = JSON.parse(contentId);
      const { serializedStream, client, seekTo } = source;
      const stream = JSON.parse(serializedStream);
      this.playbackStartPosition = seekTo;

      const {
        sourceType,
        slug,
        channel: { image_url: channelLogo } = {},
        channelInformation: { image_url: channelLogoCi } = {},
        analyticsData,
      } = stream;

      this.slug = slug;
      this.channelLogo = channelLogo || channelLogoCi;

      const {
        nielsenDemographicId,
        nielsenOptOutStatus,
        deviceId,
        deviceVersion,
        deviceFriendlyName,
        deviceModelName,
      } = source;

      this.analytics.forEach(a => {
        a.analyticsData = analyticsData;
        if (a instanceof NielsenCastAnalytics)
          a.logUpdateOtt(
            nielsenDemographicId,
            nielsenOptOutStatus,
            deviceId,
            deviceVersion,
            deviceFriendlyName,
            deviceModelName,
          );
      });

      this.onSourceTypeChange(sourceType);

      this.stopLiveTimelineUpdates();
      this.stopAustinMetadataPoll();
      this._stopRecentsUpdate();
      this.liveLinearPlaybackManager.stop();
      this.store.dispatch(resetPlayer());
      this.isAustinConfiguring = false;
      this.isAdBreakInProgress = false;

      if (isSlingSourceType(this.sourceType)) {
        this.slingSeekbarModifier.modify();
        this.isAustinLoading = true;
      } else {
        this.slingSeekbarModifier.restore();
        this.isAustinLoading = false;
      }

      CastReceiver.log(`[CAST][PLAYER_MANAGER] REQUEST_LOAD (${client}):`, source);

      await this.signIn();

      const {
        profiles: {
          currentProfile: { profileId, ageLevel, role },
        },
      } = state;
      await this.store.dispatch(setCurrentProfileId(profileId, role, ageLevel));

      this.configureMediaItem(loadRequestData.media, sourceType, stream);

      await this._setMetadata(loadRequestData.media.metadata);

      if (isLiveLinearSourceType(this.sourceType)) {
        const {
          player: { customAttributesMap },
        } = state;
        const {
          stream: { ott_mapping_id: ottMappingId } = {},
          channelInformation: { ott_mapping_id: ottMappingIdCi } = {},
        } = stream;

        const ottMapId = ottMappingId || ottMappingIdCi;

        this.liveLinearPlaybackManager.start(ottMapId, loadRequestData.media);

        if (ottMapId && isEmpty(customAttributesMap[ottMapId])) {
          await this.store.dispatch(fetchLiveVideoMetadata(ottMapId));
        }
      } else if (sourceType === 'on_demand') {
        const {
          stream: { video_id: videoId },
        } = stream;
        const {
          auth: { viewer: { analytics_id: analyticsId } = {} },
          networkInfo: { externalPublicIp },
        } = state;

        const metadata = videoId
          ? await this.store.dispatch(fetchVideoMetadata(videoId))
          : undefined;

        if (!isEmpty(metadata)) {
          const {
            custom_attributes: { freewheel_enabled: freewheelEnabled = false } = {},
          } = metadata;

          if (freewheelEnabled) {
            const freewheelAd = getFreeWheelAdVmap(metadata, analyticsId, externalPublicIp);
            await addVmapToMedia(loadRequestData.media, freewheelAd);
          }
        }
      }
      if (this.sourceType !== 'live' && this.sourceType !== 'live_linear') {
        loadRequestData.currentTime = seekTo;
      }
      await this.castPlayerHandler.loadStream(stream, this.receiverId, this.uuid);
    }
    return loadRequestData;
  };

  interceptMediaStatus = (mediaStatusData: cast.framework.messages.MediaStatus) => {
    CastReceiver.log('MEDIA_STATUS intercepted');
    if (isSlingSourceType(this.sourceType)) {
      if (
        mediaStatusData.playerState === 'PLAYING' ||
        mediaStatusData.playerState === 'BUFFERING'
      ) {
        this.isAustinConfiguring = false;
      }
      if (mediaStatusData.playerState === 'PLAYING' || mediaStatusData.playerState === 'PAUSED') {
        mediaStatusData.currentTime = this.isAustinConfiguring
          ? undefined
          : Austin.getCurrentTime() / 1000;
      }
      if (typeof mediaStatusData.media === 'object') {
        this.updateMediaInformationWithAustinData(mediaStatusData.media);
        if (this.sourceType === 'live' && this.isAustinConfiguring === false) {
          this.scheduleLiveTimelineUpdate();
        }
        let tracks = this.castPlayerHandler.getSlingSubtitleTracks();
        mediaStatusData.media.tracks = mediaStatusData.media.tracks || [];
        mediaStatusData.media.tracks = mediaStatusData.media.tracks.filter(t => t.type !== 'TEXT');
        let largestId = mediaStatusData.media.tracks.reduce(
          (maxId, { trackId }) => (maxId > trackId ? maxId : trackId),
          1,
        );
        tracks = tracks.map(t => ({
          language: 'en', // t.lang,
          name: 'English', // t.label,
          type: 'TEXT',
          trackContentType: 'text/cea608',
          trackId: ++largestId,
          bitmovinTrackId: t.id,
        }));
        mediaStatusData.media.tracks = [...mediaStatusData.media.tracks, ...tracks];
      }
    } else if (isLiveLinearSourceType(this.sourceType)) {
      this.liveLinearPlaybackManager.updateMediaStatus(mediaStatusData);
      this.scheduleLiveTimelineUpdate();
    }
    if (
      this.sourceType === 'on_demand' ||
      this.sourceType === 'live_linear' ||
      this.sourceType === 'est'
    ) {
      if (typeof mediaStatusData.media === 'object') {
        mediaStatusData.media.breakClips = undefined;
        if (!isEmpty(mediaStatusData.media.tracks)) {
          let textFound = false;
          mediaStatusData.media.tracks = mediaStatusData.media.tracks.filter(i => {
            if (i.type === 'TEXT') {
              if (!textFound) {
                textFound = true;
                i.name = 'English';
                return true;
              }
              return false;
            }
            return true;
          });
        }
      }
    }
    if (
      typeof mediaStatusData.media === 'object' &&
      this.isContentLoaded &&
      !isSlingSourceType(this.sourceType)
    ) {
      mediaStatusData.media.metadata.posterUrl = '';
    }
    return mediaStatusData;
  };

  interceptCaptions = (tracksInfo: Object) => {
    if (!isSlingSourceType(this.sourceType)) return tracksInfo;
    const { activeTrackIds: [activeId] = [] } = tracksInfo;
    if (activeId) {
      const { tracks } = this.playerManager.getMediaInformation();
      if (isEmpty(tracks)) return undefined;

      const textTrack = tracks.find(t => t.trackId === activeId);
      if (!textTrack) return undefined;

      const { bitmovinTrackId } = textTrack;
      this._enableSubtitles(bitmovinTrackId);
    } else {
      this._disableSubtitles();
    }
    return undefined;
  };

  interceptSeek = (seek: Object) => {
    if (this._isAdPlaying() || !this._seekEnabled) {
      return this.userActionNotAvailable(cast.framework.messages.Command.SEEK);
    }
    return seek;
  };

  interceptPause = (pause: Object) => {
    if (this._isAdPlaying() || !this._canPause) {
      return this.userActionNotAvailable(cast.framework.messages.Command.PAUSE);
    }
    return pause;
  };

  interceptPlay = (play: Object) => {
    if (this._isAdPlaying()) {
      return this.userActionNotAvailable(cast.framework.messages.Command.PLAY);
    }
    return play;
  };

  userActionNotAvailable(action: cast.framework.messages.Command) {
    CastReceiver.log(`[userActionNotAvailable] User is not allowed to do : ${action}`);
    const error = new cast.framework.messages.ErrorData(
      cast.framework.messages.ErrorType.INVALID_REQUEST,
    );
    error.reason = cast.framework.messages.ErrorReason.NOT_SUPPORTED;
    return error;
  }

  handlePartialData({ chunkId, chunksNumber, chunk, id }: Chunk) {
    const itWasTheLastChunk = this.messageReader.readChunk({ chunkId, chunksNumber, chunk, id });
    if (itWasTheLastChunk) {
      const payload = this.messageReader.getFullPayloadByMessageId(id);

      if (payload) {
        this.handleCustomMessage(payload);
      } else {
        console.error(
          '[CAST] Fail to handle custom messages - received empty payload from message reader.',
        );
      }
    }
  }

  configureAustin({
    title,
    subtitle,
    poster,
    serializedStream,
  }: {
    title: string,
    subtitle: string,
    poster: string,
    serializedStream: string,
  }) {
    CastReceiver.log('[CAST][AUSTIN] Received request to re-configure Austin: ', {
      title,
      subtitle,
      poster,
      serializedStream,
    });

    this.onAustinStateChange({ type: 'configure' });

    this.castPlayerHandler.loadStream(
      JSON.parse(serializedStream),
      this.receiverId,
      this.uuid,
      true,
    );
  }

  async handleCustomMessage(msg: CastSenderToReceiverMessageType) {
    CastReceiver.log('[CAST] Received a custom message:', msg);
    switch (msg.type) {
      case 'RESET_STORE': {
        const { payload } = msg;
        CastReceiver.log('[CAST] Received new store:', payload);
        this.resetStore(payload);
        break;
      }
      case 'AUSTIN_CONFIGURE': {
        clearTimeout(this.austinConfigureTimeoutId);

        if (this.isAustinLoading === true || this.isAustinConfiguring === true) {
          this.austinConfigureTimeoutId = setTimeout(() => {
            this.handleCustomMessage(msg);
          }, 2000);
          return;
        }

        const {
          payload,
          payload: { serializedStream, title, subtitle },
        } = msg;

        const stream = JSON.parse(serializedStream);
        const {
          slug,
          channel: { image_url: channelLogo } = {},
          channelInformation: { image_url: channelLogoCi } = {},
        } = stream;

        this.slug = slug;
        this.channelLogo = channelLogo || channelLogoCi;

        this.isAustinConfiguring = true;

        this.sendEmptyAustinTimeline();

        this.passEventToStateMachine('channel_change');

        const metadata = new cast.framework.messages.MovieMediaMetadata();
        metadata.title = title;
        metadata.subtitle = subtitle;
        await this._setMetadata(metadata);

        const mediaInfo = new cast.framework.messages.MediaInformation();
        mediaInfo.metadata = metadata;
        // This dummy contentId is necessary for iOS Cast SDK to apply new metadata.
        mediaInfo.contentId = 'DUMMY_CONTENT_ID';
        mediaInfo.streamType = cast.framework.messages.StreamType.LIVE;

        this.playerManager.setMediaInformation(mediaInfo);

        this.configureAustin(payload);
        break;
      }
      case 'AUSTIN_GET_DVR_INFO': {
        this.castPlayerHandler.sendDvrInfoToSenders();
        break;
      }
      case 'LL_TIME_SHIFT': {
        const { payload: offset } = msg;
        this.castPlayerHandler.timeShift(offset);
        this.syncLLInfoWithSender();
        break;
      }
      case 'LL_PAUSE': {
        this.castPlayerHandler.pauseLL();
        this.passEventToStateMachine('pause');
        break;
      }
      case 'LL_PLAY': {
        this.castPlayerHandler.resumeLL();
        this.passEventToStateMachine('PLAYING');
        break;
      }
      case 'SHUT_DOWN': {
        this.shutDownReceiver();
        break;
      }
      case 'CONFIGURE_SUBTITLES': {
        this.processConfigureSubtitlesRequest(msg.payload);
        break;
      }
      case 'SEEK_TO':
        this.castPlayerHandler.seekTo(msg.payload);
        break;
      case 'SEEK_BY':
        this.castPlayerHandler.seekBy(msg.payload);
        break;
      case 'GET_SUBTITLES_INFO':
        this.castPlayerHandler.sendInfoAboutSubtitlesToSenders();
        break;
      default:
        console.warn(`[CAST] Received unknown type of message - ${msg.type}:`, msg);
        (msg.type: empty); // eslint-disable-line no-unused-expressions
        break;
    }
  }

  syncLLInfoWithSender() {
    /* Send LL info to senders 5 times in 1...5 seconds */
    map(range(1, 6, 1), i => delay(this.castPlayerHandler.sendLLInfoToSenders, i * 1000));
  }

  onAppStateChangeEvent = (event: Object) => {
    CastReceiver.log('[CAST][PLAYER_MANAGER] Player manager event: ', event.type, event);
    let contentId;
    switch (event.type) {
      case 'REQUEST_LOAD':
        contentId = event.requestData?.media?.contentId;
        if (
          contentId === TRANSITIONS.LL_TRANSITION ||
          contentId === TRANSITIONS.LL_TO_AD_TRANSITION ||
          contentId === TRANSITIONS.AD_TO_AD_TRANSITION ||
          contentId === TRANSITIONS.AD_TO_LL_TRANSITION
        ) {
          // Do not process CAF state change on LL transition.
          return;
        }
        break;
      case 'PLAYER_LOAD_COMPLETE':
        contentId = event?.media?.contentId;
        if (
          contentId === TRANSITIONS.LL_TRANSITION ||
          contentId === TRANSITIONS.LL_TO_AD_TRANSITION ||
          contentId === TRANSITIONS.AD_TO_AD_TRANSITION ||
          contentId === TRANSITIONS.AD_TO_LL_TRANSITION
        ) {
          // Do not process CAF state change on LL transition.
          return;
        }
        if (this.sourceType === 'on_demand' || this.sourceType === 'est') {
          this._postToRecents();
          this._scheduleRecentsUpdate();
        }
        break;
      case 'REQUEST_SEEK':
        this.handleSeekRequest(event);
        break;
      case 'MEDIA_FINISHED': {
        this._stopRecentsUpdate();
        if (isSlingSourceType(this.sourceType)) {
          this.stopAustinMetadataPoll();
          this.stopLiveTimelineUpdates();
          // This should not really happen. It means the event has played till the end.
          // Normally the one second timer in the LL manager will trigger a transition.
        } else if (isLiveLinearSourceType(this.sourceType)) {
          if (event.endedReason !== 'INTERRUPTED' && event.endedReason !== 'ERROR') {
            this.liveLinearPlaybackManager.onMediaFinished();
          }
        } else if (event.endedReason === 'END_OF_STREAM') this.onPlaybackFinishedHandler();
        break;
      }
      case 'REQUEST_PAUSE': {
        this.passEventToStateMachine('pause');
        this.castPlayerHandler.onRequestPause();
        break;
      }
      case 'PAUSE': {
        if (isSlingSourceType(this.sourceType)) {
          this.stopAustinMetadataPoll();
        }
        break;
      }
      case 'REQUEST_PLAY': {
        this.castPlayerHandler.onRequestPlay();
        break;
      }
      case 'PLAYING': {
        this.isContentLoaded = true;
        cast.player.api.setLoggerLevel(cast.player.api.LoggerLevel.NONE);
        this.castPlayerHandler.hidePlayerUI();
        if (isSlingSourceType(this.sourceType)) {
          this.scheduleAustinMetadataPoll();
        } else if (isLiveLinearSourceType(this.sourceType)) {
          this.liveLinearPlaybackManager.onPlaying();
          // if (!this.liveLinearPlaybackManager.isPlayingAd) {
          // const { isLiveDone } = this.playerManager.getLiveSeekableRange() || {};
          // isLiveDone will become true when current event is no longer live
          // #getLiveSeekableRange() will return null if current event is final from the start
          // if (isLiveDone === true || isLiveDone === undefined) {
          // Commented for now. This is only relevant when starting playback
          // of an event that is already NOT LIVE. Should not happen in real life.
          // this.liveLinearPlaybackManager.liveDone();
          // }
          // }
        }
        break;
      }
      case 'BREAK_STARTED': {
        this.isAdBreakInProgress = true;
        break;
      }
      case 'BREAK_ENDED': {
        this.isAdBreakInProgress = false;
        break;
      }
      case 'LIVE_ENDED': {
        if (isLiveLinearSourceType(this.sourceType)) {
          this.liveLinearPlaybackManager.liveDone();
        }
        break;
      }
      case 'MEDIA_STATUS': {
        if (isLiveLinearSourceType(this.sourceType)) {
          // stub
        }
        break;
      }
      case 'ERROR': {
        this._stopRecentsUpdate();
        if (isLiveLinearSourceType(this.sourceType)) {
          this.liveLinearPlaybackManager.stop();
        } else if (isSlingSourceType(this.sourceType)) {
          this.stopAustinMetadataPoll();
          this.stopLiveTimelineUpdates();
          this.isAustinConfiguring = false;
          this.isAustinLoading = false;
        }
        this.store.dispatch(resetPlayer());
        break;
      }
      default:
        break;
    }

    this.onAppStateChange(event);
  };

  updateMediaInformationWithAustinData = (media: cast.framework.messages.MediaInformation) => {
    if (this.sourceType === 'dvr') {
      media.duration = this.isAustinConfiguring ? 0 : Austin.getDuration() / 1000;
      media.streamType = this.isAustinConfiguring ? 'LIVE' : 'BUFFERED';
    } else {
      media.duration = this.isAustinConfiguring ? 0 : Austin.getMaxTimeShift() / 1000;
      media.streamType = this.isAustinConfiguring ? 'LIVE' : 'BUFFERED';
    }
  };

  scheduleLiveTimelineUpdate = () => {
    this.stopLiveTimelineUpdates();
    this.austinTimelineUpdateTimerId = setTimeout(() => {
      const media = this.playerManager.getMediaInformation();
      if (media) {
        if (this.sourceType === 'live') {
          this.updateMediaInformationWithAustinData(media);
        } else if (this.sourceType === 'live_linear') {
          this.liveLinearPlaybackManager.updateMediaInformation(media);
        }
        this.playerManager.setMediaInformation(media);
      }
      if (this.isAustinConfiguring === false) {
        this.scheduleLiveTimelineUpdate();
      }
    }, 3000);
  };

  stopLiveTimelineUpdates = () => {
    clearTimeout(this.austinTimelineUpdateTimerId);
  };

  _scheduleRecentsUpdate = () => {
    this._stopRecentsUpdate();
    this.putToRecentsIntervalId = setInterval(this._putToRecents, 5000);
  };

  _stopRecentsUpdate = () => {
    clearInterval(this.putToRecentsIntervalId);
  };

  sendEmptyAustinTimeline = () => {
    const media = this.playerManager.getMediaInformation();
    if (!media) return;
    media.duration = 0;
    media.streamType = 'LIVE';
    this.playerManager.setMediaInformation(media);
  };

  handleSeekRequest = (event: { requestData: { currentTime: number } }) => {
    const { currentTime } = event.requestData;
    this.castPlayerHandler.seekTo(currentTime);
  };

  signIn = async () =>
    this.isSignedIn ? Promise.resolve() : this.getRadishSession({ isCacheLogin: false });

  setIsSignedIn(isSignedIn: boolean) {
    this.isSignedIn = isSignedIn;
  }

  onSourceTypeChange(sourceType: SourceType) {
    this.sourceType = sourceType;
  }

  onReceiverChange(receiverId: string) {
    this.receiverId = receiverId;
  }

  onUuidChange(uuid: string) {
    this.uuid = uuid;
  }

  setTimeAliveTimer(nMinutes: number = 0) {
    if (nMinutes > 0) CastReceiver.log(`[CAST] I'm alive for ${nMinutes} minutes.`);
    setTimeout(() => this.setTimeAliveTimer(nMinutes + 1), 60 * 1000);
  }

  shutDownReceiver() {
    CastReceiver.log('[CAST] Shutting down receiver app.');
    const context = getCastReceiverContext();
    context.stop();
  }

  processConfigureSubtitlesRequest(conf: SubtitlesConf) {
    const result = this._isPossibleToConfigureSubtitles();
    if (result.possible) this.configureSubtitles(conf);
    else console.warn(`[CAST][SUBTITLES] Fail to configure subtitles - ${result.reason}`);
  }

  configureSubtitles(conf: SubtitlesConf) {
    if (conf.enabled) this._enableSubtitles(conf.trackId);
    else this._disableSubtitles();
  }

  scheduleAustinMetadataPoll() {
    this.stopAustinMetadataPoll();
    this.austinMetadataPollTimerId = setTimeout(() => {
      this.syncMediaInformation();
      this.scheduleAustinMetadataPoll();
    }, 30000);
  }

  stopAustinMetadataPoll() {
    if (this.austinMetadataPollTimerId) {
      clearTimeout(this.austinMetadataPollTimerId);
    }
  }

  async syncMediaInformation() {
    CastReceiver.log('[CAST][AUSTIN] Attempt to update media info');
    const mediaInfoRequest: AustinProgramInfoRequest = await this.castPlayerHandler.getAustinProgramInfo();

    if (mediaInfoRequest.result === 'ok') {
      CastReceiver.log('[CAST][AUSTIN] Received updated program info from Austin');

      const {
        info: {
          program_title: programTitle,
          event_name: eventName,
          content_id: contentId,
          mediaview: [{ title: episodeTitle }] = [{}],
        },
      } = mediaInfoRequest;

      const franchiseName = programTitle || eventName;

      const newSlingEventInfo = {
        programTitle: franchiseName,
        episodeTitle,
        contentId,
      };

      if (
        franchiseName === 'No Info Available' ||
        isEqual(this.currentSlingEventInfo, newSlingEventInfo)
      ) {
        return;
      }
      this.currentSlingEventInfo = newSlingEventInfo;

      if (!!franchiseName && !!contentId) {
        //                               /
        //                    __       //
        //                    -\= \=\ //
        //                  --=_\=---//=--
        //                -_==/  \/ //\/--
        //                 ==/   /O   O\==--
        //    _ _ _ _     /_/    \  ]  /--
        //   /\ ( (- \    /       ] ] ]==-
        //  (\ _\_\_\-\__/     \  (,_,)--       ... Be the slug!
        // (\_/                 \     \-
        // \/      /       (   ( \  ] /)
        // /      (         \   \_ \./ )
        // (       \         \      )  \
        // (       /\_ _ _ _ /---/ /\_  \
        //  \     / \     / ____/ /   \  \
        //   (   /   )   / /  /__ )   (  )
        //   (  )   / __/ '---`       / /
        //   \  /   \ \             _/ /
        //   ] ]     )_\_         /__\/
        //   /_\     ]___\
        //  (___)
        this.slug = `${franchiseName.replace(/\W/g, '').replace(/\s/g, '_')}_e${contentId.replace(
          /.*-([^-]+)-.*/,
          '$1',
        )}`;
      }

      const metadata = new cast.framework.messages.MovieMediaMetadata();
      metadata.title = franchiseName;
      metadata.subtitle = episodeTitle;
      await this._setMetadata(metadata);

      const mediaInfo = new cast.framework.messages.MediaInformation();
      mediaInfo.metadata = metadata;
      mediaInfo.streamType = cast.framework.messages.StreamType.LIVE;
      // This dummy contentId is necessary for iOS Cast SDK to apply new metadata.
      mediaInfo.contentId = 'DUMMY_CONTENT_ID';

      CastReceiver.log('[CAST][AUSTIN] Send request to update media info: ', metadata);
      this.playerManager.setMediaInformation(mediaInfo);

      this.analytics.forEach(analytic => analytic.onSlingMediaInfoChanged());
    } else {
      console.warn(
        '[CAST][AUSTIN] Fail to sync media info "Austin.getProgramInfo" has returned an error: ',
        mediaInfoRequest.reason,
      );
    }
  }

  _isPossibleToConfigureSubtitles(): { possible: true } | { possible: false, reason: string } {
    if (!this.castPlayerHandler.slingPlayer)
      return { possible: false, reason: `the player is not initialized.` };
    return { possible: true };
  }

  _enableSubtitles(trackId: string) {
    const player = this.castPlayerHandler.slingPlayer;
    if (!player) return;
    this.castPlayerHandler.tweakSubtitlesContainerToMakeItWorkProperly();
    player.subtitles.enable(trackId);
    CastReceiver.log(`[CAST][SUBTITLES] Enabled subtitles: trackId - ${trackId}`);
  }

  _disableSubtitles() {
    const player = this.castPlayerHandler.slingPlayer;
    if (!player) return;
    const tracks = player.subtitles.list() || [];
    tracks.forEach(({ id: trackId }) => {
      player.subtitles.disable(trackId);
      CastReceiver.log(`[CAST][SUBTITLES] Disabled subtitles trackID - ${trackId}`);
    });
  }

  setLiveLinearAdSlotInfoForAnalytics(adSlotInfo: Array<AdInfo>) {
    if (Array.isArray(this.analytics)) {
      this.analytics.forEach(analytic => {
        if (analytic instanceof BaseCastAnalytics) {
          analytic.liveLinearAdSlotInfo = adSlotInfo;
        }
      });
    } else {
      console.warn('[CAST][ANALYTICS] Failed to set ads slot info : ', adSlotInfo);
    }
  }

  updateLlConfig(canPause: boolean, seekEnabled: boolean) {
    this._canPause = canPause;
    this._seekEnabled = seekEnabled;
  }
}

export { ChromeCastReceiver };
